import { uniqueId } from 'lodash';
import React, { useRef, useState, useEffect } from 'react';

import type { SvgIconProps } from '@mui/material';
import type { Theme, SxProps } from '@mui/material/styles';
import { Send, Check, ExpandMore, ExpandLess } from '@mui/icons-material';
import {
  Box,
  Card,
  Grow,
  Stack,
  styled,
  Popper,
  Button,
  Collapse,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';

import useAxios from 'src/hooks/use-axios';

import { fadeInUpAnimation } from 'src/utils/animation';

import type { Paginated, ChatConversation } from 'src/types/api';

type Message = {
  isMe: boolean;
  text: string;
  id: string;
};

export const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMe',
})<{ isMe: boolean }>(({ isMe }) => ({
  display: 'flex',
  margin: 0,
  padding: '0.5em 1em 0.65em',
  borderRadius: 4,
  background: isMe ? '#CE153F' : '#FFFFFF',
  color: isMe ? '#FFFFFF' : '#2E2E2E',
  maxWidth: '85%',
  clear: 'both',
  position: 'relative',
  wordWrap: 'break-word',
  alignSelf: isMe ? 'end' : 'start',
  width: isMe ? 'auto' : 'fit-content',
  ...fadeInUpAnimation,

  // Base border radius
  borderTopLeftRadius: '1em',
  borderTopRightRadius: '1em',
  borderBottomLeftRadius: isMe ? '1em' : 0,
  borderBottomRightRadius: isMe ? 0 : '1em',
  marginBottom: 7,
  marginTop: 3,
}));

const LoadingDots = () => {
  const size = 8;
  const color = 'text.secondary';
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        p: 0.5,
        ...fadeInUpAnimation,
      }}
    >
      <Box
        sx={{
          width: size,
          height: size,
          bgcolor: color,
          borderRadius: '50%',
          mr: 0.5,
          animation: 'loadingDots 1.4s infinite ease-in-out',
          '@keyframes loadingDots': {
            '0%, 80%, 100%': {
              opacity: 0.3,
              transform: 'scale(0.8)',
            },
            '40%': {
              opacity: 1,
              transform: 'scale(1)',
            },
          },
        }}
      />
      <Box
        sx={{
          width: size,
          height: size,
          bgcolor: color,
          borderRadius: '50%',
          mr: 0.5,
          animation: 'loadingDots 1.4s infinite ease-in-out 0.2s',
        }}
      />
      <Box
        sx={{
          width: size,
          height: size,
          bgcolor: color,
          borderRadius: '50%',
          animation: 'loadingDots 1.4s infinite ease-in-out 0.4s',
        }}
      />
    </Box>
  );
};

type Props = {
  open?: boolean;
  conversation_id?: string;
  bigChat?: boolean;
  inviteChat?: boolean;
  sx?: SxProps<Theme>;
};

const Chat = ({ open, conversation_id, bigChat, inviteChat, sx }: Props) => {
  const api = useAxios();

  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [expandedChat, setExpandedChat] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [awaitingResponse, setAwaitingResponse] = useState(false);
  const [loadingNewMessages, setLoadingNewMessages] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(true);
  const [chatHistory, setChatHistory] = useState({ total: 0, current: 0, firstCall: true });

  const previousScrollHeightRef = useRef<number | null>();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const baseMessages: Message[] = [
    {
      isMe: false,
      text: "Hello! My name is Megi. I'm a virtual assistant designed to help you learn about pregnancy. My goal is to provide clear and helpful information for your peace of mind :)",
      id: uniqueId('chat'),
    },
    {
      isMe: false,
      text: "What's on your mind today?",
      id: uniqueId('chat'),
    },
  ];

  useEffect(() => {
    getHistory();
    if (conversation_id) setMessages(baseMessages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open) {
      scrollContainerRef.current!.scroll({ top: 0, behavior: 'smooth' });
    }
  }, [open]);

  useEffect(() => {
    if (awaitingResponse) scrollContainerRef.current!.scroll({ top: 0, behavior: 'smooth' });
  }, [awaitingResponse]);

  useEffect(() => {
    if (loadingNewMessages || !previousScrollHeightRef.current) return;

    scrollContainerRef.current!.scrollTop = previousScrollHeightRef.current;
    previousScrollHeightRef.current = null;
  }, [loadingNewMessages]);

  const getHistory = () => {
    if (conversation_id) return;
    if (loadingNewMessages || (!chatHistory.firstCall && chatHistory.current >= chatHistory.total)) return;

    setLoadingNewMessages(true);
    api.get<Paginated<ChatConversation>>(`v1/cora_chat/?page_size=1&page=${chatHistory.current + 1}`).then((res) => {
      setChatHistory((prev) => ({
        total: res.data.count,
        current: prev.current + 1,
        firstCall: false,
      }));
      const messageHistory = res?.data?.results?.[0]?.messages;
      if (messageHistory) {
        setMessages((prev) => {
          let newMessages = messageHistory.map((message) => ({
            isMe: message.role === 'user',
            text: message.content,
            id: uniqueId('chat'),
          }));
          if (chatHistory.current + 1 === chatHistory.total) newMessages = baseMessages.concat(newMessages);

          return newMessages.concat(prev);
        });
      } else {
        setExpandedChat(true);
        setMessages(baseMessages);
      }
      setLoadingNewMessages(false);
    });
  };

  const handleNewMessage = (preset?: string) => {
    if (awaitingResponse || (!preset && !newMessage)) return;
    setExpandedChat(true);
    setShowRecommendations(false);
    setNewMessage('');
    const timeoutDelay = setTimeout(() => setAwaitingResponse(true), preset ? 0 : 500);

    setMessages((prev) => [
      ...prev,
      {
        isMe: true,
        text: preset || newMessage,
        id: uniqueId('chat'),
      },
    ]);

    api.post<{ message: string }>('v1/cora_chat/', { input_message: preset || newMessage, conversation_id }).then((res) => {
      clearTimeout(timeoutDelay);
      setAwaitingResponse(false);
      setMessages((prev) => [
        ...prev,
        {
          isMe: false,
          text: res.data.message,
          id: uniqueId('chat'),
        },
      ]);
    });
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    if (target.scrollHeight - target.clientHeight + target.scrollTop < 250) {
      previousScrollHeightRef.current = scrollContainerRef.current!.scrollTop;
      getHistory();
    }
  };

  return (
    <Stack
      component={Card}
      sx={{
        width: { xs: '100vw', sm: '70vw', md: '50vw', lg: '35vw', xl: '25vw' },
        overflow: 'hidden',
        pb: 1,
        backgroundColor: 'brand.chat',
        ...sx,
        // borderBottomRightRadius: 0,
      }}
    >
      <Stack
        sx={{
          flexShrink: 0,
          backgroundColor: 'brand.blue',
          height: '3em',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {!bigChat && (
          <IconButton
            onClick={() => setExpandedChat((prev) => !prev)}
            sx={{ position: 'absolute', right: 0, color: 'common.white' }}
          >
            {/* if inviteChat is true, icons are reversed because it expands in the other way */}
            {expandedChat !== inviteChat ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        )}
        <Typography variant="h5" color="common.white">
          MEGI
        </Typography>
      </Stack>

      <Collapse in={expandedChat || bigChat} timeout={500} sx={{ ...(bigChat && { display: 'flex', flex: 1 }) }}>
        <Stack
          sx={{
            height: bigChat ? '100%' : '60vh',
            width: '100%',
          }}
        >
          {showDisclaimer && (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                top: 0,
                p: 1.5,
                backgroundColor: (theme) => `${theme.palette.brand.main}77`,
                backdropFilter: 'blur(20px)',
                zIndex: 1,
                // mt: '3em',
                ...(bigChat && { textAlign: 'center' }),
              }}
            >
              <Typography variant="h6" color="brand.blue">
                Educational Tool for Doulas
              </Typography>
              <Stack
                sx={{
                  flexDirection: 'row',
                  gap: { sm: 1 },
                  ...(bigChat && { alignItems: 'center', flexDirection: 'column' }),
                }}
              >
                <Typography variant="body2" fontWeight="600" color="brand.blue">
                  This AI assistant is designed to serve as an educational tutor to support you in your work. The
                  information provided is for learning and informational purposes only.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    color: 'brand.blue',
                    borderColor: 'brand.blue',
                    maxWidth: '3em',
                    height: '2.5em',
                    ...(!bigChat && { alignSelf: 'end' }),
                  }}
                  onClick={() => setShowDisclaimer(false)}
                >
                  <Check />
                </Button>
              </Stack>
            </Box>
          )}

          <Stack
            onScroll={handleScroll}
            ref={scrollContainerRef}
            sx={{
              flexDirection: 'column-reverse',
              width: '100%',
              flex: 1,
              px: 2,
              overflowY: 'scroll',
              scrollbarGutter: 'stable',
              scrollbarColor: (theme) => `${theme.palette.brand.blue} ${theme.palette.brand.chat}`,
            }}
          >
            {awaitingResponse ? (
              <MessageBubble isMe={false}>
                <LoadingDots />
              </MessageBubble>
            ) : null}

            {messages.toReversed().map((message, i) => (
              <MessageBubble key={message.id} isMe={message.isMe}>
                <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-line' }}>
                  {message.text}
                </Typography>
              </MessageBubble>
            ))}
          </Stack>

          <Button sx={{ borderRadius: 0, mt: -1 }} onClick={() => setShowRecommendations((prev) => !prev)}>
            {showRecommendations ? <ExpandMore /> : <ExpandLess />}
          </Button>

          <Collapse in={showRecommendations}>
            <Stack
              sx={{
                mb: 1,
                px: 2,
                justifyContent: 'center',
                flexDirection: { sm: 'row' },
                gap: { xs: 2, md: 5 },
                ...(!bigChat && { flexDirection: 'column', gap: 1 }),
              }}
            >
              {[
                'How do I support a client during an induction?',
                'What are effective comfort measures for back labor?',
                'What should be in my doula bag?',
              ].map((question, i) => (
                <Button
                  disabled={awaitingResponse}
                  key={i}
                  sx={{ borderRadius: 3, border: '1px solid' }}
                  onClick={() => handleNewMessage(question)}
                >
                  {question}
                </Button>
              ))}
            </Stack>
          </Collapse>
        </Stack>
      </Collapse>

      <TextField
        value={newMessage}
        multiline
        onChange={(e) => setNewMessage(e.target.value)}
        fullWidth
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleNewMessage();
          }
        }}
        placeholder="Let's chat about the Megi course..."
        sx={{
          px: 2,
          pt: expandedChat ? 0 : 1,
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: 'brand.main',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'brand.main',
            },
          },
        }}
        slotProps={{
          input: {
            endAdornment: (
              <Send
                sx={{ cursor: 'pointer' }}
                color={
                  // TODO: tighten — 'brand' is not an augmented SvgIcon color
                  awaitingResponse || !newMessage ? 'disabled' : ('brand' as unknown as SvgIconProps['color'])
                }
                onPointerDown={(e: React.PointerEvent<SVGSVGElement>) => {
                  e.preventDefault();
                  handleNewMessage();
                }}
              />
            ),
            sx: { backgroundColor: 'white', borderRadius: 4 },
          },
        }}
      />
    </Stack>
  );
};

export const ChatButton = () => {
  const [chatOpen, setChatOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (buttonRef.current) {
      setChatOpen(true);
    }
  }, []);

  return (
    <>
      <IconButton
        onClick={() => setChatOpen((prev) => !prev)}
        ref={buttonRef}
        sx={{
          position: 'fixed',
          bottom: 25,
          right: 25,
          backdropFilter: 'blur(1px)',
          backgroundColor: (theme) => `${theme.palette.brand.chat}99`,
          border: (theme) => `${theme.palette.brand.chat} 1px solid`,
        }}
      >
        <Box height="2em" component="img" src="/favicon/apple-touch-icon.png" />
      </IconButton>
      <Popper
        keepMounted
        anchorEl={buttonRef.current}
        placement="top-start"
        open={chatOpen}
        transition
        sx={{ zIndex: 999 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'bottom right' }}>
            <Box mr={{ sm: '3.5em' }} mb={{ sm: '-0.5em' }}>
              <Chat open={chatOpen} />
            </Box>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default Chat;
